import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { Scale, AlertTriangle, CheckCircle2, Loader2, Info, Lock } from 'lucide-react';

const ENTITLEMENT_CATEGORIES = [
  { value: 'EOT', label: 'Extension of Time (EOT)' },
  { value: 'Cost', label: 'Additional Cost' },
  { value: 'EOT + Cost', label: 'EOT + Cost (Both)' },
];

const NOTICE_TYPES = [
  { value: 'Notice of Delay', label: 'Notice of Delay' },
  { value: 'Notice of Intention to Claim', label: 'Notice of Intention to Claim' },
  { value: 'Compensation Event Notification', label: 'Compensation Event Notification' },
  { value: 'Early Warning Notice', label: 'Early Warning Notice' },
  { value: 'Notice of Suspension', label: 'Notice of Suspension' },
  { value: 'Notice of Force Majeure', label: 'Notice of Force Majeure' },
];

export default function ContractualClassificationPanel({ event, projectContractType, canEditEvent, onClassified, contractClauses }) {
  const [isClassifying, setIsClassifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clauseOptions, setClauseOptions] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);

  const [form, setForm] = useState({
    contract_type: projectContractType || 'JBCC',
    contract_version: '',
    contractual_basis: '',
    clause_reference: '',
    clause_title: '',
    notice_type: '',
    entitlement_category: '',
    ongoing_or_ceased: event?.has_not_ceased === false ? 'ceased' : 'ongoing',
    date_became_known: event?.start_date ? event.start_date.split('T')[0] : '',
    cease_date: event?.delay_ceased_date ? event.delay_ceased_date.split('T')[0] : '',
    notice_required: true,
    further_particulars_required: false,
    notes: '',
    event_family: event?.event_family || '',
    event_sub_type: event?.event_sub_type || '',
  });

  const classification = event?.contractual_classification;
  const isPending = event?.notice_generation_locked || event?.classification_status === 'pending';
  const isCompleted = event?.classification_status === 'completed' || classification?.clause_assigned;
  const isManualEvent = !event?.event_source || event.event_source === 'manual';

  // If it's a manual event and not locked, don't show the section prominently
  const showSection = !isManualEvent || isCompleted;

  const [suggestedClauses, setSuggestedClauses] = useState([]);

  useEffect(() => {
    if (event?.project_id) {
      api.events.getClauseOptions(event.project_id, 'notice').then(res => {
        setClauseOptions(res.data?.clause_options || []);
        if (res.data?.contract_type) {
          setForm(prev => ({ ...prev, contract_type: res.data.contract_type }));
        }
      }).catch(() => {});
    }
    api.events.getTaxonomy().then(res => {
      setTaxonomy(res.data?.taxonomy || []);
    }).catch(() => {});
  }, [event?.project_id]);

  // Load clause suggestions when family or contract type changes
  useEffect(() => {
    if (form.contract_type && form.event_family && isClassifying) {
      api.events.getClauseSuggestions(form.contract_type, form.event_family).then(res => {
        setSuggestedClauses(res.data?.suggested_clause_refs || []);
      }).catch(() => setSuggestedClauses([]));
    }
  }, [form.contract_type, form.event_family, isClassifying]);

  const selectedFamilyData = taxonomy.find(t => t.family === form.event_family);

  const handleClauseSelect = (clauseStr) => {
    // Parse "Clause X.Y - Title" format
    const parts = clauseStr.split(' - ');
    setForm(prev => ({
      ...prev,
      clause_reference: clauseStr,
      clause_title: parts.length > 1 ? parts.slice(1).join(' - ') : '',
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.contract_type) return toast.error('Please select a contract type');
    if (!form.clause_reference) return toast.error('Please select a contract clause');
    if (!form.entitlement_category) return toast.error('Please select an entitlement category');
    if (!form.date_became_known) return toast.error('Please enter the date the event became known');
    if (!form.ongoing_or_ceased) return toast.error('Please select ongoing or ceased status');

    setSaving(true);
    try {
      await api.events.classify(event.id, form);
      toast.success('Contractual classification saved. Notice generation is now unlocked.');
      setIsClassifying(false);
      onClassified?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save classification');
    } finally {
      setSaving(false);
    }
  };

  // If manual and not classified, show a minimal info box
  if (isManualEvent && !isCompleted && !isPending) {
    return null;
  }

  return (
    <div className={`border rounded-sm shadow-sm p-6 ${
      isPending ? 'bg-amber-50 border-amber-300 border-l-4 border-l-amber-500' :
      isCompleted ? 'bg-green-50 border-green-200' :
      'bg-white border-slate-200'
    }`} data-testid="contractual-classification-section">
      <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-slate-700" />
          <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Contractual Classification</h2>
          {isPending && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs ml-2">
              <Lock className="h-3 w-3 mr-1" /> Required
            </Badge>
          )}
          {isCompleted && (
            <Badge className="bg-green-100 text-green-700 border-green-300 text-xs ml-2">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Classified
            </Badge>
          )}
        </div>
        {isPending && canEditEvent && !isClassifying && (
          <Button
            size="sm"
            onClick={() => setIsClassifying(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm text-xs uppercase"
            data-testid="start-classification-btn"
          >
            <Scale className="h-3.5 w-3.5 mr-1" />
            Classify Now
          </Button>
        )}
      </div>

      {/* Warning banner for pending classification */}
      {isPending && !isClassifying && (
        <div className="flex items-start gap-3 p-4 bg-amber-100 border border-amber-300 rounded-sm mb-4" data-testid="classification-pending-warning">
          <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Contractual classification is required</p>
            <p className="text-xs text-amber-700 mt-1">
              This event was created from <span className="font-semibold">{(event.event_source || 'unknown').replace(/_/g, ' ')}</span>. 
              Before a notice can be generated, you must assign the applicable contract, clause, notice type, and entitlement category.
            </p>
            <p className="text-xs text-amber-600 mt-2">
              Notice generation is <strong>locked</strong> until this classification is completed.
            </p>
          </div>
        </div>
      )}

      {/* Completed classification display */}
      {isCompleted && !isClassifying && classification && (
        <div className="space-y-3" data-testid="classification-completed-display">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Contract Type</p>
              <p className="text-sm text-slate-700 font-medium">{classification.contract_type}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Clause Reference</p>
              <p className="text-sm text-slate-700 font-mono">{classification.clause_reference}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notice Type</p>
              <p className="text-sm text-slate-700">{classification.notice_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Entitlement Category</p>
              <Badge className={`text-xs ${
                classification.entitlement_category === 'EOT + Cost' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                classification.entitlement_category === 'Cost' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                'bg-teal-100 text-teal-700 border-teal-200'
              }`}>{classification.entitlement_category || 'N/A'}</Badge>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Status</p>
              <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                {classification.ongoing_or_ceased === 'ceased' ? 'Ceased' : 'Ongoing'}
              </Badge>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Date Became Known</p>
              <p className="text-sm text-slate-700 font-mono">
                {classification.date_became_known ? new Date(classification.date_became_known).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Classified By</p>
              <p className="text-sm text-slate-700">{classification.assigned_by_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Classification Date</p>
              <p className="text-sm text-slate-700 font-mono">
                {classification.assigned_date ? new Date(classification.assigned_date).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
          {classification.notes && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</p>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{classification.notes}</p>
            </div>
          )}
          {canEditEvent && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setForm({
                  ...form,
                  contract_type: classification.contract_type || projectContractType || 'JBCC',
                  clause_reference: classification.clause_reference || '',
                  clause_title: classification.clause_title || '',
                  notice_type: classification.notice_type || '',
                  entitlement_category: classification.entitlement_category || '',
                  ongoing_or_ceased: classification.ongoing_or_ceased || 'ongoing',
                  date_became_known: classification.date_became_known ? classification.date_became_known.split('T')[0] : '',
                  cease_date: classification.cease_date ? classification.cease_date.split('T')[0] : '',
                  notes: classification.notes || '',
                  event_family: event.event_family || '',
                  event_sub_type: event.event_sub_type || '',
                });
                setIsClassifying(true);
              }}
              className="rounded-sm text-xs mt-2"
              data-testid="edit-classification-btn"
            >
              Edit Classification
            </Button>
          )}
        </div>
      )}

      {/* Classification form */}
      {isClassifying && (
        <div className="space-y-4" data-testid="classification-form">
          {/* Event Family & Sub-Type (can be updated during classification) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Event Family</Label>
              <Select value={form.event_family} onValueChange={(v) => setForm(prev => ({ ...prev, event_family: v, event_sub_type: '' }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-event-family">
                  <SelectValue placeholder="Select event family..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {taxonomy.map(t => (
                    <SelectItem key={t.family} value={t.family}>{t.family}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Event Sub-Type</Label>
              <Select value={form.event_sub_type} onValueChange={(v) => setForm(prev => ({ ...prev, event_sub_type: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-event-subtype">
                  <SelectValue placeholder="Select sub-type..." />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {(selectedFamilyData?.sub_types || []).map(st => (
                    <SelectItem key={st} value={st}>{st}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <hr className="border-slate-200" />

          {/* Contract & Clause Selection — same as manual creation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contract Type *</Label>
              <Select value={form.contract_type} onValueChange={(v) => setForm(prev => ({ ...prev, contract_type: v, clause_reference: '', clause_title: '' }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-contract-type">
                  <SelectValue placeholder="Select contract type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JBCC">JBCC</SelectItem>
                  <SelectItem value="FIDIC">FIDIC</SelectItem>
                  <SelectItem value="NEC3">NEC3</SelectItem>
                  <SelectItem value="NEC4">NEC4</SelectItem>
                  <SelectItem value="GCC">GCC</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contract Version</Label>
              <Input
                value={form.contract_version}
                onChange={(e) => setForm(prev => ({ ...prev, contract_version: e.target.value }))}
                placeholder="e.g., PBA 6.2, Red Book 1999"
                className="rounded-sm"
                data-testid="classify-contract-version"
              />
            </div>
          </div>

          {/* Clause Selection — reuses same clause definitions */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Contract Clause *</Label>
            {suggestedClauses.length > 0 && (
              <p className="text-xs text-blue-600 mb-2 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Suggested clauses for <span className="font-semibold">{form.event_family}</span> are highlighted
              </p>
            )}
            <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-200 rounded-sm p-2" data-testid="classification-clause-list">
              {(contractClauses[form.contract_type] || contractClauses.JBCC || []).map((clause) => {
                const clauseStr = `${clause.clause} - ${clause.title}`;
                const isSelected = form.clause_reference === clauseStr;
                const isSuggested = suggestedClauses.some(s => clause.clause.includes(s));
                return (
                  <div
                    key={clause.id}
                    className={`p-2.5 rounded border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                        : isSuggested
                        ? 'border-blue-400 bg-blue-50/50 hover:border-blue-500'
                        : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50/50'
                    }`}
                    onClick={() => handleClauseSelect(clauseStr)}
                    data-testid={`classify-clause-${clause.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`mt-0.5 h-3.5 w-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                        isSelected ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                      }`}>
                        {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-xs text-slate-900">{clause.clause} — {clause.title}</p>
                          {isSuggested && <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] px-1.5 py-0">Suggested</Badge>}
                        </div>
                        <p className="text-xs text-slate-500">{clause.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notice Type & Entitlement */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Notice Type</Label>
              <Select value={form.notice_type} onValueChange={(v) => setForm(prev => ({ ...prev, notice_type: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-notice-type">
                  <SelectValue placeholder="Select notice type" />
                </SelectTrigger>
                <SelectContent>
                  {NOTICE_TYPES.map(nt => (
                    <SelectItem key={nt.value} value={nt.value}>{nt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Entitlement Category *</Label>
              <Select value={form.entitlement_category} onValueChange={(v) => setForm(prev => ({ ...prev, entitlement_category: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-entitlement">
                  <SelectValue placeholder="Select entitlement" />
                </SelectTrigger>
                <SelectContent>
                  {ENTITLEMENT_CATEGORIES.map(ec => (
                    <SelectItem key={ec.value} value={ec.value}>{ec.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ongoing/Ceased & Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Ongoing / Ceased *</Label>
              <Select value={form.ongoing_or_ceased} onValueChange={(v) => setForm(prev => ({ ...prev, ongoing_or_ceased: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="classify-ongoing-ceased">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ongoing">Ongoing</SelectItem>
                  <SelectItem value="ceased">Ceased</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Date Became Known *</Label>
              <Input
                type="date"
                value={form.date_became_known}
                onChange={(e) => setForm(prev => ({ ...prev, date_became_known: e.target.value }))}
                className="rounded-sm"
                data-testid="classify-date-known"
              />
            </div>
            {form.ongoing_or_ceased === 'ceased' && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Cease Date</Label>
                <Input
                  type="date"
                  value={form.cease_date}
                  onChange={(e) => setForm(prev => ({ ...prev, cease_date: e.target.value }))}
                  className="rounded-sm"
                  data-testid="classify-cease-date"
                />
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Classification Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="rounded-sm"
              placeholder="Any additional notes about the contractual basis..."
              data-testid="classify-notes"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm text-xs uppercase"
              data-testid="save-classification-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Save Classification & Unlock Notice
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsClassifying(false)}
              className="rounded-sm text-xs"
            >
              Cancel
            </Button>
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              Once classification is saved, notice generation will be unlocked and the same contractual basis, clause, and time-bar deadlines from the contract engine will apply.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
